import { persist } from 'zustand/middleware';
import { create } from 'zustand';
import { type GameState } from '@shared/modelTypes/game-types';
import { teamNames, teamRoles, type MemberId, type TeamName, type TeamRole } from '@shared/modelTypes/session-types';
import type { Location } from '@shared/modelTypes/location-types';
import { closeSocket, initSocket } from '@/socket';
import { useEffect, useState } from 'react';
import { api, navigateToDesiredUrl } from '@/api';
import { getTimeProgress } from '@shared/util';
import { serverNow } from '@/engine/clock';
import { abortRepair, extendRepairTime, sendChatMessage, startShift, validateCode, validateConnectionCode } from '@/engine/actions';
import type { TeamData } from '@/routes/game';
import { Vars } from '@shared/game-variables';
import { type TimerOpts } from '@/engine/hooks';
import type { Result } from '@shared/modelTypes/util-types';
import type { ChatData } from '@/components/shared_ui/Chat';
import { toast } from 'sonner';
import { playSound } from '@/engine/audio';

export type PlayerState = {
	id: MemberId;
	team: TeamName;
	teamRole: TeamRole;
};

type Store = {
	/**
	 * Persistent uuid to associate with this device
	 *
	 * It is generated once and kept in localStorage
	 */
	deviceId: string;

	/**
	 * Our current location, persists across play sessions but may be changes by the user
	 *
	 * Will be null on first boot and populated through the `/set-location` route.
	 */
	location: Location | null;

	/**
	 * Our current player state, will be null on first boot and populated through the `/set-team` route.
	 *
	 * NOTE: playerState is cleared alongSide the gameState when you leave the game.
	 */
	playerState: PlayerState | null;

	/**
	 * Represents the entire state of the current game session, regardless of who you are playing as.
	 *
	 * `playerState` may be used to get information that is relevant to the current player.
	 */
	gameState: GameState | null;
};

function actions(set: (partial: Partial<Store> | ((state: Store) => Partial<Store>)) => void) {
	return {
		setDeviceId: (deviceId: string) => {
			console.log('UPDATE DEVICE_ID', { deviceId });
			return set({ deviceId });
		},
		setPlayerState: (playerState: PlayerState) => {
			console.log('UPDATE PLAYER_STATE', { playerState });

			set({ playerState });

			navigateToDesiredUrl();
		},
		unsetPlayerState: () => {
			console.log('UNSET PLAYER_STATE');
			// redirect after state change

			set({ playerState: null });

			navigateToDesiredUrl();
		},
		setGameState: (gameState: GameState) => {
			console.log('UPDATE GAME_STATE', { gameState });
			set({ gameState });

			navigateToDesiredUrl();
		},
		leaveLocation: () => {
			closeSocket();

			set({
				location: null,
				gameState: null,
				playerState: null,
			});

			navigateToDesiredUrl();
		},
		setLocationSessionId: (sessionId: string) => {
			console.log('UPDATE LOCATION_SESSION_ID');

			set((state) => {
				return {
					location: state.location ? { ...state.location, sessionId: sessionId } : null,
				};
			});
		},
		setLocation: (location: Location) => {
			console.log('UPDATE LOCATION');

			set({
				location,
				gameState: null,
				playerState: null,
			});

			if (!location.id) {
				closeSocket();
				// redirect after state change
				navigateToDesiredUrl();
			} else {
				initSocket(location.id);
			}
		},
		leaveSession: () => {
			set((state) => {
				return {
					gameState: null,
					playerState: null,
					location: state.location ? { ...state.location, sessionId: null } : null,
				};
			});

			navigateToDesiredUrl();
		},
	};
}

export const useStore = create<Store & ReturnType<typeof actions>>()(
	persist(
		(set) => ({
			deviceId: '',
			location: null,
			playerState: null,
			gameState: null,
			...actions(set),
		}),
		{
			name: 'monteursgame-store',
		},
	),
);

export const useDeviceId = () => {
	return useStore((state) => state.deviceId);
};

export const useSessionId = () => {
	const sessionId = useStore((state) => state.location?.sessionId);
	// TODO: consider if we should throw here
	// currently this can be triggered if you leave a session while on a screen that calls useSessionId
	// because first the screen re-renders and only after does it redirect
	if (!sessionId) throw new Error('No Session ID');

	return sessionId;
};

export const usePlayLocation = () => {
	const location = useStore((state) => state.location);

	return location;
};

export const usePlayerState = () => {
	const playerState = useStore((state) => state.playerState);

	return {
		...playerState,
		isOperator: playerState?.teamRole === 'operator',
		isTechTeam: playerState?.teamRole && playerState.teamRole !== 'operator',
	};
};

export const useTeamState = () => {
	const player = usePlayerState()!;
	// const members = useMembers();
	const teamState = useStore((state) => state.gameState?.teamStates[player.team!])!;

	// if (!player || !teamState) return null;

	const team: TeamData = {
		name: player.team ?? '',
		score: teamState?.score ?? 0,
		// members: members.filter((m) => m.team == player.team),
	};

	return team;
};

export const useGameState = () => {
	const gameState = useStore((state) => state.gameState);

	return gameState;
};

export const useMembers = () => {
	const members = useStore((state) => state.gameState?.members) || [];

	return members;
};

export const useMember = (memberId: string | null) => {
	const members = useMembers();

	if (!memberId) return null;
	const member = members.find((m) => m.memberId == memberId);
	if (!member) return null;

	return member;
};

export const useAvailableTeamTechs = () => {
	const rooms = useRooms();
	const members = useMembers();
	const playerState = usePlayerState();
	if (!playerState) return [];

	const teamTechs = members.filter((m) => {
		if (m.team !== playerState.team) return false;
		if (m.role == 'operator') return false;
		const isInRoom = rooms.some((r) => r.assignedMemberId == m.memberId);
		if (isInRoom) return false;
		return true;
	});

	return teamTechs;
};

const useChat = (techTeamRole: TeamRole) => {
	const player = usePlayerState()!;

	const chatId: MemberId = `${player.team!}-${techTeamRole}`;

	const messages = useStore((state) => state.gameState?.chats?.[chatId]) || [];

	const chatData: ChatData = {
		chatId: chatId,
		playerRole: player.teamRole!,
		chattingWithRole: player.teamRole == 'operator' ? techTeamRole : 'operator',
		messages: messages,
	};

	return {
		data: chatData,
		sendMessage: async (message: string) => {
			await sendChatMessage(chatId, message);
		},
	};
};

export const useTeamSelection = () => {
	const members = useMembers();
	const maxPlayerCount = teamRoles.length;
	const teamStates = teamNames.map((teamName) => {
		let existingPlayerCount = 0;
		for (const member of members) {
			if (member.team == teamName && member.deviceId !== null) existingPlayerCount++;
		}
		const joinable = existingPlayerCount < maxPlayerCount;

		return { name: teamName, joinable, existingPlayerCount, maxPlayerCount };
	});

	return teamStates;
};

export const useRoleSelection = (teamName: TeamName) => {
	const members = useMembers();
	const teamMembers = members.filter((m) => m.team == teamName);

	const roleStates = teamRoles.map((role) => {
		const available = !teamMembers.some((m) => m.role == role && m.deviceId !== null);

		return { name: role, available };
	});

	return roleStates;
};

export const useRooms = () => {
	const rooms = useStore((state) => state.gameState?.rooms);

	if (!rooms) return [];

	return rooms;
};

export const useRoomState = (roomId: string) => {
	const rooms = useRooms();
	const room = rooms.find((r) => r.id == roomId);
	if (!room) return null;

	type TimeData = {
		startupTimeLeft: number;
		startupProgress: number;
		repairingTimeLeft: number;
		repairingProgress: number;
	};
	const [timeData, setTimeData] = useState<TimeData>({
		startupTimeLeft: 0,
		startupProgress: 0,
		repairingTimeLeft: 0,
		repairingProgress: 0,
	});

	useEffect(() => {
		if (room.status !== 'startup' && room.status !== 'repairing') return;

		const timer = setInterval(() => {
			const startUptimeProgress = room.status == 'startup' ? getTimeProgress(room.startupTime!, room.startRepairingTime!, serverNow()) : null;
			const repairTimeProgress =
				room.status == 'repairing' ? getTimeProgress(room.startRepairingTime!, room.endRepairingTime!, serverNow()) : null;
			setTimeData({
				startupTimeLeft: startUptimeProgress?.timeLeft || 0,
				startupProgress: startUptimeProgress?.progress || 0,
				repairingTimeLeft: repairTimeProgress?.timeLeft || 0,
				repairingProgress: repairTimeProgress?.progress || 0,
			});
		}, 1_000);
		return () => clearInterval(timer);
	}, [timeData, room.status]);

	const assignedMember = useMember(room.assignedMemberId ?? null);
	const availableTechs = useAvailableTeamTechs();

	const assignedRoom = {
		...room,
		...timeData,
		availableTechs,
		assignedMember,
		canAssignMember: room.status == 'startup' && !room.assignedMemberId,
	};

	return assignedRoom;
};

export const useConnectionCodes = () => {
	const connectionCodes = useStore((state) => state.gameState?.connectionCodes) || [];

	return connectionCodes;
};

export const useBonusCodes = () => {
	const bonusCodes = useStore((state) => state.gameState?.bonusCodes) || [];

	return bonusCodes;
};

export const useTechTeamStatus = (roleId: TeamRole) => {
	const bonusCodes = useStore((s) => s.gameState?.bonusCodes);
	const gameOverTime = useStore((s) => s.gameState?.gameOverTime);
	const player = usePlayerState()!;

	const memberId: MemberId = `${player.team!}-${roleId}`;

	// NOTE: member may not exist is no player joined at this team/role
	const member = useStore((s) => s.gameState?.members.find((m) => m.memberId === memberId)) ?? null;
	const assignedRoom = useStore((s) => s.gameState?.rooms.find((m) => m.assignedMemberId === memberId));

	const connectionTime = member?.connectionTime ?? serverNow();

	const connection: TimerOpts = {
		from: connectionTime,
		// TODO: extract constant for connection timer / decide duration elsewhere
		to: connectionTime + Vars.connectionDuration,
		// this will freeze the timer at the end of the game if it is set
		progress: gameOverTime,
	};

	return {
		memberId: memberId,
		team: player.team,
		role: roleId,
		canAssignedToRoom: member && (!assignedRoom || !assignedRoom.id) && member.shiftStarted,
		connection: connection,
		assignedRoom: {
			id: assignedRoom?.id ?? null,
			name: assignedRoom?.name ?? 'None',
			repairTime: assignedRoom?.repairTime ?? 0,
			status: assignedRoom?.status ?? 'not-assigned',
			extendedTimeCount: assignedRoom?.extendedTimeCount ?? 0,
			activationCodes: assignedRoom?.activationCodes,
			repairCodes: assignedRoom?.repairCodes,
			notAssigned: !assignedRoom,
			starting: assignedRoom && assignedRoom.status == 'startup',
			repairing: assignedRoom && assignedRoom.status == 'repairing',
			startupTime: {
				from: assignedRoom?.startupTime ?? 0,
				to: assignedRoom?.startRepairingTime ?? 0,
				progress: gameOverTime,
			},
			repairingTime: {
				from: assignedRoom?.startRepairingTime ?? 0,
				to: assignedRoom?.endRepairingTime ?? 0,
				progress: gameOverTime,
			},
		},
		validateCode: async (code: string) => {
			if (bonusCodes?.includes(code.toUpperCase().replace(/ /g, ''))) {
				const result = await validateCode(code);
				if (result.success) {
					toast.success('Correcte code, extra opdracht succesvol uitgevoerd!');
					playSound({ name: 'success' });
					return { success: true, message: 'Scan validated successfully' };
				}
				toast.error('Fout, code is al ingevoerd!');
				playSound({ name: 'failed' });
				return { success: false, message: 'Bonus code has already been used' };
			}

			// check if user is assigned to room and room status is connected to validation
			if (!assignedRoom) {
				toast.error('Geen issue gekoppeld aan tech team');
				return { success: false, message: 'User not assigned to room' };
			}
			if (!(assignedRoom.status == 'repairing')) {
				toast.error('Tech team niet in reparatie modus');
				return { success: false, message: 'Room status not connected to validation' };
			}

			const now = serverNow();

			// check if time is expired (extra check, status should already have been updated if time has expired)
			if (!assignedRoom.endRepairingTime || now > assignedRoom.endRepairingTime) {
				toast.error('Tijd voorbij, niet meer mogelijk reparatie uit te voeren!');
				return { success: false, message: 'Room time over' };
			}

			// validate code based on room status
			if (!assignedRoom.repairCodes.map((c) => c.toUpperCase().replace(/ /g, '')).includes(code.toUpperCase().replace(/ /g, ''))) {
				toast.success('Incorrecte code, reparatie niet gelukt!');
				return { success: false, message: 'Code not found' };
			}

			// validate code by server
			const result = await validateCode(code, memberId);
			if (result.success) {
				toast.success('Correcte code, reparatie succesvol uitgevoerd!');
				return { success: true, message: 'Scan validated successfully' };
			} else {
				return result;
			}
		},
	};
};
// TODO: consider concrete type
export type TechTeamStatus = ReturnType<typeof useTechTeamStatus>;

export const useOperatorView = () => {
	const teamInfo = useTeamState()!;

	const [selectedTechTeam, setSelectedTechTeam] = useState<TeamRole>('tech-team-one');

	const notSelectedTechTeam = selectedTechTeam == 'tech-team-one' ? 'tech-team-two' : 'tech-team-one';

	const chat = useChat(selectedTechTeam);
	const techTeamStatus = useTechTeamStatus(selectedTechTeam);

	const switchSelectedTechTeam = () => {
		setSelectedTechTeam(notSelectedTechTeam);
	};

	const switchTechTeam = {
		selectedTechTeam,
		notSelectedTechTeam,
		switchSelectedTechTeam,
	};

	return {
		techTeamStatus: techTeamStatus,
		chat: chat,
		teamInfo: teamInfo,
		switchTechTeam,
	};
};

export const useTechTeamView = () => {
	const player = usePlayerState()!;
	const sessionId = useSessionId();
	const member = useStore((s) => s.gameState?.members.find((m) => m.memberId === player.id))!;
	const connectionCodes = useConnectionCodes();

	const status = useTechTeamStatus(player.teamRole!);

	const chat = useChat(player.teamRole!);

	const assignedRoom = status.assignedRoom;
	const canAbortRepair = assignedRoom && assignedRoom.status === 'repairing';
	const canExtendRepairTime =
		assignedRoom && assignedRoom.status === 'repairing' && assignedRoom.extendedTimeCount < Vars.maxTimeExtendCount;

	return {
		sessionId: sessionId,
		memberId: member.memberId,
		shiftStarted: member.shiftStarted,
		status: status,
		chat: chat,

		canExtendRepairTime: canExtendRepairTime,
		canAbortRepair: canAbortRepair,

		onScan: async (code: string): Promise<Result> => {
			if (connectionCodes.includes(code)) {
				const result = await validateConnectionCode(code);
				if (result.success) {
					return { success: true };
				} else {
					return result;
				}
			}

			const now = serverNow();

			// check if user connection time is expired
			if (now > status.connection.to) {
				return { success: false, message: 'User connection lost' };
			}

			// check if user is assigned to room and room status is connected to validation
			if (!assignedRoom) return { success: false, message: 'User not assigned to room' };
			if (!(assignedRoom.status == 'startup')) {
				await api.eventLogCreate({
					eventType: 'WRONG_SCAN',
					playerId: member.memberId,
					sessionId: sessionId,
					data: {
						code: code,
					},
				});
				return { success: false, message: 'Room status not connected to validation' };
			}

			// check if time is expired (extra check, status should already have been updated if time has expired)
			if (now > assignedRoom.startupTime.to) return { success: false, message: 'Room time over' };

			// validate code based on room status
			if (!assignedRoom.activationCodes?.includes(code)) {
				await api.eventLogCreate({
					eventType: 'WRONG_SCAN',
					playerId: member.memberId,
					sessionId: sessionId,
					data: {
						code: code,
					},
				});

				return { success: false, message: 'Code not found' };
			}

			// validate code by server
			const result = await validateCode(code);
			if (result.success) {
				return { success: true, message: 'Scan validated successfully' };
			} else {
				return result;
			}
		},

		startShift: () => {
			if (member.shiftStarted) return;
			startShift();
		},

		abortRepair: () => {
			if (!assignedRoom || assignedRoom.status !== 'repairing') return;
			abortRepair();
		},
		extendRepairTime: () => {
			if (!canExtendRepairTime) return;
			extendRepairTime();
		},
	};
};
export type TechTeamView = ReturnType<typeof useTechTeamView>;

export const useGameEndView = () => {
	const gameState = useStore((state) => state.gameState);

	const startTime = gameState?.startTime ?? serverNow();
	const endTime = gameState?.gameOverTime ?? serverNow();
	const playDuration = (endTime - startTime) / 1000;

	return {
		sessionId: gameState?.sessionId,
		damage: gameState?.systemDamage ?? 0,
		startTime: startTime,
		endTime: endTime,
		playDuration: playDuration,
		teams: teamNames.map((teamName) => {
			const state = gameState?.teamStates[teamName];
			return {
				team: teamName,
				score: state?.score ?? 0,
			};
		}),
	};
};

export const useObservedGameView = () => {
	const gameState = useGameState();

	return gameState;
};

const initialState = useStore.getState();

// generate a device id if not set yet
// NOTE: we do this here because zustand does not persist the initial state, it only persists after an action has been performed
if (!initialState.deviceId) {
	initialState.setDeviceId(crypto.randomUUID());
}

if (initialState.location?.id) {
	initSocket(initialState.location.id);
}
